import * as THREE from "three";
import { useEffect, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { RoundedBox, Environment } from "@react-three/drei";
import useIsMobile from "../hooks/useIsMobile";

function SpinningGlassCube({ position }) {
  const ref = useRef();

  const phase = useRef(Math.random() * 10).current;
  const speed = 0.4;

  function easeInOutCubic(x) {
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
  }

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const fadeInDuration = 10;
    const progress = Math.min(t / fadeInDuration, 1);
    const fade = easeInOutCubic(progress);

    const rotationProgress = (t + phase) * speed * fade;

    if (ref.current) {
      ref.current.rotation.x = rotationProgress;
      ref.current.rotation.y = rotationProgress;
    }
  });

  return (
    <RoundedBox
      ref={ref}
      args={[1.2, 1.2, 1.2]}
      radius={0.07}
      smoothness={2}
      position={position}
    >
      <meshPhysicalMaterial
        color="#18555c"
        roughness={0}
        transmission={1}
        thickness={1}
        ior={1.5}
        transparent={true}
        opacity={0.8}
        metalness={1}
        clearcoat={1}
        clearcoatRoughness={0}
        reflectivity={0}
      />
    </RoundedBox>
  );
}

function GlassCubeGrid() {
  const isMobile = useIsMobile();
  const group = useRef();
  const currentAxis = useRef(randomAxis());
  const targetAxis = useRef(randomAxis());
  const transitionTimer = useRef(0);
  const switchInterval = 3;
  const spacing = 1.8;

  useFrame((state, delta) => {
    transitionTimer.current += delta;

    if (transitionTimer.current > switchInterval) {
      transitionTimer.current = 0;
      targetAxis.current = randomAxis();
    }

    currentAxis.current.lerp(targetAxis.current, delta);
    currentAxis.current.normalize();

    if (group.current) {
      group.current.rotateOnAxis(currentAxis.current, delta * 1);
    }
  });

  const positions = [-1, 0, 1];

  const meshRef = useRef();

  const [isDragging, setIsDragging] = useState(false);
  const previous = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleMove = (clientX, clientY) => {
      if (!isDragging) return;

      const deltaX = clientX - previous.current.x;
      const deltaY = clientY - previous.current.y;

      previous.current = { x: clientX, y: clientY };

      if (meshRef.current) {
        // Rotate around Y (left-right drag)
        meshRef.current.rotation.y += deltaX * 0.01;

        // Rotate around X (up-down drag)
        meshRef.current.rotation.x += deltaY * 0.01;
      }
    };

    const handlePointerMove = (e) => handleMove(e.clientX, e.clientY);
    const handleTouchMove = (e) => {
      if (e.touches.length === 1) {
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const stopDragging = () => {
      setIsDragging(false);
      if (isMobile) {
        document.body.style.overflow = "auto";
      }
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopDragging);
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", stopDragging);
    window.addEventListener("touchcancel", stopDragging);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopDragging);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", stopDragging);
      window.removeEventListener("touchcancel", stopDragging);
    };
  }, [isDragging, isMobile]);

  const startDragging = (clientX, clientY) => {
    previous.current = { x: clientX, y: clientY };
    setIsDragging(true);
    if (isMobile) {
      document.body.style.overflow = "hidden";
    }
  };

  const handlePointerDown = (e) => {
    e.stopPropagation();
    startDragging(e.clientX, e.clientY);
  };

  const handleTouchStart = (e) => {
    e.stopPropagation();
    if (e.touches.length === 1) {
      startDragging(e.touches[0].clientX, e.touches[0].clientY);
    }
  };
  return (
    <group
      ref={meshRef}
      onPointerDown={handlePointerDown}
      onTouchStart={handleTouchStart}
    >
      <group ref={group}>
        {positions.flatMap((x) =>
          positions.flatMap((y) =>
            positions.map((z) => (
              <SpinningGlassCube
                key={`${x}${y}${z}`}
                position={[x * spacing, y * spacing, z * spacing]}
              />
            ))
          )
        )}
      </group>
    </group>
  );
}

function randomAxis() {
  return new THREE.Vector3(
    Math.random(),
    Math.random(),
    Math.random()
  ).normalize();
}

export default function CubeGridBg({ cameraPosition = 15 }) {
  return (
    <>
      <Canvas
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          backgroundColor: "transparent",
        }}
        camera={{ position: [0, 0, cameraPosition], fov: 50 }}
      >
        <ambientLight intensity={0.3} />
        <directionalLight position={[10, 10, 10]} intensity={0.8} />
        <Environment
          files={"https://static.card.casa/external/potsdamer_platz_1k.hdr"}
        />
        <GlassCubeGrid />
      </Canvas>
    </>
  );
}
